import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Spin, message } from "antd";
import {
  BankOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
  PlayCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { usePartnershipContext } from "../../../core/context/PartnershipContext";
import { cascadeService, CascadeFlowDto, TierDto } from "./CascadeService";
import styles from "./CascadeView.module.css";

function progressColor(pct: number): string {
  if (pct >= 100) return "#4CAF50";
  if (pct > 0) return "#F26522";
  return "rgba(255,255,255,0.20)";
}

// BE sends `status` and `outputAvailable`; FE interface uses `projectStatus`/`hasK1Output`
function resolvedStatus(tier: TierDto): string | null {
  return tier.projectStatus ?? tier.status ?? null;
}

function resolvedHasK1Output(tier: TierDto): boolean {
  return tier.hasK1Output || (tier.outputAvailable ?? false);
}

function tierStatusIcon(tier: TierDto): { icon: React.ReactNode; label: string } {
  const totalCalcs = tier.totalCalcs ?? 0;
  const completedCalcs = tier.completedCalcs ?? 0;
  const allComplete = totalCalcs > 0 && completedCalcs >= totalCalcs;

  if (resolvedHasK1Output(tier) || allComplete)
    return { icon: <CheckCircleOutlined style={{ color: "#4CAF50" }} />, label: "K-1 Output available" };
  const status = resolvedStatus(tier);
  if (status === "InProgress")
    return { icon: <LoadingOutlined spin style={{ color: "#F26522" }} />, label: "Calculation running" };
  if (status === "Completed" || status === "Complete")
    return { icon: <CheckCircleOutlined style={{ color: "#4CAF50" }} />, label: "Calculation complete" };
  if (status === "Failed")
    return { icon: <ExclamationCircleOutlined style={{ color: "#ff6b6b" }} />, label: "Calculation failed" };
  if (tier.projectId)
    return { icon: <ClockCircleOutlined style={{ color: "rgba(255,255,255,0.45)" }} />, label: "Not started" };
  return { icon: <ExclamationCircleOutlined style={{ color: "rgba(255,165,0,0.7)" }} />, label: "Waiting for project setup" };
}

function flowStatusIcon(status: string | undefined, isFlowing: boolean): React.ReactNode {
  const s = isFlowing ? "Flowing" : (status ?? "Pending");
  if (s === "Complete") return <CheckCircleOutlined style={{ color: "#4CAF50", marginRight: 4 }} />;
  if (s === "Flowing") return <SyncOutlined spin style={{ color: "#F26522", marginRight: 4 }} />;
  return <MinusCircleOutlined style={{ color: "rgba(255,255,255,0.35)", marginRight: 4 }} />;
}

function tierChildLabel(tierLevel: number, isLastTier: boolean, count: number): string | null {
  if (count === 0) return null;
  if (tierLevel === 1) return `${count} sub-fund${count !== 1 ? "s" : ""}`;
  if (isLastTier) return `${count} partner${count !== 1 ? "s" : ""}`;
  return `${count} ${count === 1 ? "entity" : "entities"}`;
}

function FlowArrow({ tier, isFlowing }: { tier: TierDto; isFlowing: boolean }) {
  const status = tier.flowStatus;
  const badgeClass =
    status === "Complete"
      ? styles.flowStatusComplete
      : status === "Flowing" || isFlowing
        ? styles.flowStatusFlowing
        : styles.flowStatusPending;

  const statusLabel = isFlowing ? "Flowing" : (status ?? "Pending");

  return (
    <div className={styles.flowArrowWrap}>
      <div className={`${styles.flowLine}${isFlowing ? ` ${styles.flowLineAnimated}` : ""}`} />
      <div className={styles.flowArrowHead} />
      <div className={styles.flowLabel}>
        <span className={styles.flowText}>K-1 → Income Input</span>
        <span className={`${styles.flowStatusBadge} ${badgeClass}`}>
          {flowStatusIcon(status ?? undefined, isFlowing)}{statusLabel}
        </span>
        {tier.flowRowCount != null && tier.flowRowCount > 0 && (
          <span>{tier.flowRowCount} rows</span>
        )}
      </div>
    </div>
  );
}

function TierCard({
  tier,
  isRoot,
  isLastTier,
  childCount,
  onViewProject,
  onViewK1Output,
  onCreateProject,
  onViewMainProject,
}: {
  tier: TierDto;
  isRoot: boolean;
  isLastTier: boolean;
  childCount: number;
  onViewProject: () => void;
  onViewK1Output: () => void;
  onCreateProject: () => void;
  onViewMainProject: () => void;
}) {
  const totalCalcs = tier.totalCalcs ?? 0;
  const completedCalcs = tier.completedCalcs ?? 0;
  const pct = totalCalcs > 0
    ? Math.round((completedCalcs / totalCalcs) * 100)
    : 0;
  const color = progressColor(pct);
  const { icon, label } = tierStatusIcon(tier);
  const childLabel = tierChildLabel(tier.tierLevel, isLastTier, childCount);

  return (
    <div className={styles.tierCard}>
      <div className={styles.tierCardTop}>
        <div className={styles.tierNameRow}>
          <span className={styles.tierIcon}><BankOutlined /></span>
          <span className={styles.tierName} title={tier.entityName}>{tier.entityName}</span>
        </div>
        <div className={styles.tierMeta}>
          <span className={styles.tierBadge}>Tier {tier.tierLevel}</span>
          {childLabel && (
            <span className={styles.partnerBadge}>{childLabel}</span>
          )}
        </div>
      </div>

      <div className={styles.progressSection}>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${pct}%`, background: pct > 0 ? color : "transparent" }}
          />
        </div>
        <span className={styles.progressLabel}>
          {totalCalcs > 0
            ? `${completedCalcs}/${totalCalcs} complete`
            : "Not started"}
        </span>
      </div>

      <div className={styles.statusRow}>
        <span className={styles.statusIcon}>{icon}</span>
        <span>{label}</span>
      </div>

      <div className={styles.cardActions}>
        {tier.projectId ? (
          <button className={styles.btnPrimary} onClick={onViewProject}>
            View Project
          </button>
        ) : isRoot ? (
          <button className={styles.btnPrimary} onClick={onViewMainProject}>
            View Main Project
          </button>
        ) : (
          <button className={styles.btnPrimary} onClick={onCreateProject}>
            Create Project
          </button>
        )}
        {resolvedHasK1Output(tier) && (
          <button className={styles.btnSecondary} onClick={onViewK1Output}>
            View K-1 Output
          </button>
        )}
      </div>
    </div>
  );
}

interface RunProgress {
  step: number;
  total: number;
  stepText: string;
  done: boolean;
  error: string | null;
}

export const CascadeView: React.FC = () => {
  const navigate = useNavigate();
  const { selectedPartnership, taxYear } = usePartnershipContext();

  const [data, setData] = useState<CascadeFlowDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [progress, setProgress] = useState<RunProgress | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [activeFlowTierIndex, setActiveFlowTierIndex] = useState<number | null>(null);
  const abortRef = useRef(false);
  const tierRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());

  const fetchFlow = useCallback(async () => {
    if (!selectedPartnership) return;
    setIsLoading(true);
    try {
      const result = await cascadeService.getCascadeFlow(selectedPartnership.id, taxYear);
      console.log("[Cascade] tiers:", result?.tiers?.map(t => ({
        tierLevel: t.tierLevel,
        entityId: t.entityId,
        entityName: t.entityName,
        projectId: t.projectId,
        completedCalcs: t.completedCalcs,
        totalCalcs: t.totalCalcs,
        status: t.status,
        outputAvailable: t.outputAvailable,
      })));
      setData(result);
    } catch {
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [selectedPartnership, taxYear]);

  useEffect(() => {
    fetchFlow();
  }, [fetchFlow]);

  const scrollToTier = (level: number) => {
    const firstOfTier = sortedTiers.find(t => t.tierLevel === level);
    if (firstOfTier) {
      tierRefs.current.get(firstOfTier.entityId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const pollUntilComplete = async (projectId: number): Promise<boolean> => {
    for (let i = 0; i < 60; i++) {
      if (abortRef.current) return false;
      await new Promise<void>((r) => setTimeout(r, 2000));
      try {
        const { status } = await cascadeService.getProjectStatus(projectId);
        if (status === "Completed") return true;
        if (status === "Failed" || status === "Error") return false;
      } catch {
        // continue polling on transient errors
      }
    }
    return false;
  };

  const handleRunCascade = async () => {
    if (!data || !selectedPartnership) return;
    const tiersWithProjects = data.tiers.filter((t) => t.projectId != null);
    if (tiersWithProjects.length === 0) {
      message.warning("No projects set up for this cascade. Create projects first.");
      return;
    }

    const totalSteps = tiersWithProjects.length * 2 - 1;
    abortRef.current = false;
    setIsRunning(true);
    setActiveFlowTierIndex(null);
    setProgress({ step: 0, total: totalSteps, stepText: "Preparing tier flow...", done: false, error: null });
    setRunModalOpen(true);

    let stepNum = 0;

    for (let i = 0; i < tiersWithProjects.length; i++) {
      const tier = tiersWithProjects[i];
      if (abortRef.current) break;

      stepNum++;
      setProgress({
        step: stepNum,
        total: totalSteps,
        stepText: `Running Tier ${tier.tierLevel} Income Allocation — ${tier.entityName}...`,
        done: false,
        error: null,
      });

      try {
        await cascadeService.runCalculation(tier.projectId!);
      } catch {
        setProgress({
          step: stepNum,
          total: totalSteps,
          stepText: `Step ${stepNum}/${totalSteps}: Failed to start Tier ${tier.tierLevel} calculation`,
          done: false,
          error: `Failed to start calculation for Tier ${tier.tierLevel} (${tier.entityName}).`,
        });
        setIsRunning(false);
        return;
      }

      const completed = await pollUntilComplete(tier.projectId!);
      if (!completed) {
        setProgress({
          step: stepNum,
          total: totalSteps,
          stepText: `Tier ${tier.tierLevel} calculation failed or timed out`,
          done: false,
          error: `Tier ${tier.tierLevel} (${tier.entityName}) calculation did not complete.`,
        });
        setIsRunning(false);
        return;
      }

      if (i < tiersWithProjects.length - 1) {
        stepNum++;
        setActiveFlowTierIndex(i);
        const nextTier = tiersWithProjects[i + 1];
        setProgress({
          step: stepNum,
          total: totalSteps,
          stepText: `Flowing K-1 data from Tier ${tier.tierLevel} → Tier ${nextTier.tierLevel}...`,
          done: false,
          error: null,
        });

        try {
          await cascadeService.executeFlow(selectedPartnership.id, taxYear);
        } catch {
          setProgress({
            step: stepNum,
            total: totalSteps,
            stepText: `Flow failed from Tier ${tier.tierLevel} → Tier ${nextTier.tierLevel}`,
            done: false,
            error: `Failed to flow K-1 data to Tier ${nextTier.tierLevel}.`,
          });
          setIsRunning(false);
          setActiveFlowTierIndex(null);
          return;
        }

        setActiveFlowTierIndex(null);
      }
    }

    setProgress({
      step: totalSteps,
      total: totalSteps,
      stepText: "Tier flow complete!",
      done: true,
      error: null,
    });
    setIsRunning(false);
    await fetchFlow();
  };

  const handleCloseModal = () => {
    if (isRunning) {
      abortRef.current = true;
      setIsRunning(false);
    }
    setRunModalOpen(false);
    setProgress(null);
    setActiveFlowTierIndex(null);
  };

  if (!selectedPartnership) {
    return (
      <div className={styles.glassWrapper}>
        <div className={styles.emptyState}>Select a partnership to view the tier flow.</div>
      </div>
    );
  }

  const partnershipName = data?.partnershipName ?? selectedPartnership.name;
  const totalTiers = data?.totalTiers ?? 0;
  const progressPct = progress
    ? Math.round((progress.step / Math.max(1, progress.total)) * 100)
    : 0;

  const sortedTiers = data
    ? [...data.tiers].sort((a, b) => a.tierLevel - b.tierLevel || a.entityId - b.entityId)
    : [];
  const maxTierLevel = sortedTiers.length > 0
    ? Math.max(...sortedTiers.map(t => t.tierLevel))
    : 1;

  const getChildCount = (entityId: number): number => {
    if (!data?.flows) return 0;
    return data.flows.filter(f => f.toEntityId === entityId).length;
  };

  const uniqueLevels = Array.from(new Set(sortedTiers.map(t => t.tierLevel))).sort((a, b) => a - b);
  const countAtLevel = (level: number) => sortedTiers.filter(t => t.tierLevel === level).length;
  const hasProjectAtLevel = (level: number) => sortedTiers.some(t => t.tierLevel === level && t.projectId);

  return (
    <div className={styles.glassWrapper}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.pageTitle}>{partnershipName} — Tier Flow</div>
          <div className={styles.pageSubtitle}>
            {taxYear}
            {totalTiers > 0 ? ` · ${totalTiers} tier${totalTiers !== 1 ? "s" : ""}` : ""}
          </div>
        </div>
        <button
          className={styles.btnRun}
          onClick={handleRunCascade}
          disabled={isRunning || isLoading}
        >
          <PlayCircleOutlined />
          Run Tier Flow
        </button>
      </div>

      {/* Two-column layout: tier cards + sticky nav */}
      <div className={styles.cascadeLayout}>
        <div className={styles.cascadeContent}>
          {isLoading ? (
            <div className={styles.loadingState}><Spin /></div>
          ) : !data || data.tiers.length === 0 ? (
            <div className={styles.emptyState}>
              No tier structure found for this partnership.
            </div>
          ) : (
            <div className={styles.tierContainer}>
              {sortedTiers.map((tier, idx) => (
                <React.Fragment key={tier.entityId}>
                  <div
                    id={`tier-${tier.entityId}`}
                    ref={el => { tierRefs.current.set(tier.entityId, el); }}
                  >
                    <TierCard
                      tier={tier}
                      isRoot={tier.tierLevel === 1}
                      isLastTier={tier.tierLevel === maxTierLevel}
                      childCount={getChildCount(tier.entityId)}
                      onViewProject={() => {
                        if (tier.projectId) navigate(`/calculations/project/${tier.projectId}`);
                      }}
                      onViewK1Output={() => {
                        if (tier.projectId) navigate(`/calculations/project/${tier.projectId}`);
                      }}
                      onCreateProject={() => navigate("/calculations/project")}
                      onViewMainProject={() => navigate("/calculations/project")}
                    />
                  </div>
                  {idx < sortedTiers.length - 1 && (
                    <FlowArrow
                      tier={tier}
                      isFlowing={activeFlowTierIndex === idx}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Sticky tier nav — one pill per unique tier level */}
        {!isLoading && uniqueLevels.length > 0 && (
          <nav className={styles.tierNav}>
            {uniqueLevels.map(level => {
              const count = countAtLevel(level);
              const hasProject = hasProjectAtLevel(level);
              return (
                <button
                  key={level}
                  className={styles.tierNavPill}
                  onClick={() => scrollToTier(level)}
                  title={`Tier ${level} — ${count} ${count === 1 ? "entity" : "entities"}`}
                >
                  <div className={styles.tierNavPillRow}>
                    <span
                      className={styles.tierNavDot}
                      style={{ background: hasProject ? "#F26522" : "rgba(255,255,255,0.25)" }}
                    />
                    <span className={styles.tierNavLabel}>Tier {level}</span>
                  </div>
                  <div className={styles.tierNavCount}>
                    {count} {count === 1 ? "entity" : "entities"}
                  </div>
                </button>
              );
            })}
          </nav>
        )}
      </div>

      {/* Run Cascade modal */}
      <Modal
        title="Run Tier Flow"
        open={runModalOpen}
        onCancel={handleCloseModal}
        footer={
          progress?.done || progress?.error
            ? [
                <button
                  key="close"
                  className={styles.btnPrimary}
                  onClick={handleCloseModal}
                  style={{ padding: "0 20px", height: 34, borderRadius: 6, fontSize: 13 }}
                >
                  Close
                </button>,
              ]
            : null
        }
        closable={!isRunning}
        maskClosable={!isRunning}
        styles={{ body: { background: "transparent" } }}
      >
        <div className={styles.modalBody}>
          {progress && (
            <>
              <div className={styles.modalProgress}>
                <div
                  className={styles.modalProgressFill}
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              {progress.error ? (
                <div className={styles.modalErrorText}>{progress.error}</div>
              ) : progress.done ? (
                <div className={styles.modalSuccessText}>
                  <CheckCircleOutlined style={{ color: "#4CAF50", marginRight: 6 }} />
                  All tiers complete!
                </div>
              ) : (
                <>
                  <div className={styles.modalStepText}>{progress.stepText}</div>
                  <div className={styles.modalStepCounter}>
                    Step {progress.step} of {progress.total}
                  </div>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Spin size="small" />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};
